import { useRef, useState, useEffect } from 'react';

import { css } from '@compiled/react';
import { UniformSlot } from '@uniformdev/canvas-react';

import { media } from '@atlaskit/primitives';

import { Image } from '@/libraries/design/components/image/src/image';
import { wacToken } from '@/libraries/design/foundations';

const uniformSlotContainerCss = css({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	height: '620px',
	maxHeight: '620px',
	overflow: 'hidden',
	position: 'relative',
	[media.above.sm]: {
		height: '688px',
		maxHeight: '688px',
	},
	[media.above.md]: {
		height: '700px',
		maxHeight: '700px',
	},
});

const UniformRovoChatContainer = () => {
	const contentContainerRef = useRef<HTMLDivElement | null>(null);

	const [interacting, setInteracting] = useState(false);
	const interactingRef = useRef(interacting);

	useEffect(() => {
		interactingRef.current = interacting;
	}, [interacting]);

	// On-load
	useEffect(() => {
		let hasRun = false;
		let observer: IntersectionObserver | null = null;
		let cleanup: (() => void) | undefined;
		let wheelTimeout: ReturnType<typeof setTimeout> | null = null;
		let keyTimeout: ReturnType<typeof setTimeout> | null = null;
		const container = contentContainerRef.current;

		function runEffectLogic() {
			if (hasRun) return;
			hasRun = true;

			const handleFirstInteraction = () => {
				if (!interactingRef.current) {
					setInteracting(true);
				}
			};

			const handleFirstClick = () => {
				handleFirstInteraction();
				enableScrollEvents();
			};

			// Scroll blocking
			function preventDefault(e: Event) {
				e.preventDefault();
				handleFirstInteraction();
			}

			function onWheel(e: WheelEvent) {
				preventDefault(e);
				if (wheelTimeout) clearTimeout(wheelTimeout);
				wheelTimeout = setTimeout(enableScrollEvents, 20);
			}

			function onKeyDown(e: KeyboardEvent) {
				preventDefault(e);
				if (keyTimeout) clearTimeout(keyTimeout);
				keyTimeout = setTimeout(enableScrollEvents, 20);
			}

			function onTouchMove(e: TouchEvent) {
				preventDefault(e);
			}

			function onTouchEnd() {
				enableScrollEvents();
			}

			function blockScrollEvents() {
				window.addEventListener('wheel', onWheel, { passive: false });
				window.addEventListener('touchmove', onTouchMove, {
					passive: false,
				});
				window.addEventListener('touchend', onTouchEnd, {
					passive: false,
				});
				window.addEventListener('touchcancel', onTouchEnd, {
					passive: false,
				});
				window.addEventListener('keydown', onKeyDown, {
					passive: false,
				});
			}

			function enableScrollEvents() {
				window.removeEventListener('wheel', onWheel);
				window.removeEventListener('touchmove', onTouchMove);
				window.removeEventListener('touchend', onTouchEnd);
				window.removeEventListener('touchcancel', onTouchEnd);
				window.removeEventListener('keydown', onKeyDown);
				if (wheelTimeout) clearTimeout(wheelTimeout);
				if (keyTimeout) clearTimeout(keyTimeout);
			}

			blockScrollEvents();

			// Content container click interactions
			if (container) {
				container.addEventListener('click', handleFirstClick, {
					once: true,
				});
			}

			// Also listen for global clicks as fallback
			const handleGlobalClick = () => {
				handleFirstInteraction();
				window.removeEventListener('click', handleGlobalClick);
			};
			window.addEventListener('click', handleGlobalClick);

			// Store cleanup function
			cleanup = () => {
				enableScrollEvents();
				if (container) {
					container.removeEventListener('click', handleFirstClick);
				}
				window.removeEventListener('click', handleGlobalClick);
			};
		}

		// Intersection Observer setup
		observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0];
				if (entry && entry.isIntersecting) {
					runEffectLogic();
					observer?.disconnect();
				}
			},
			{ threshold: 0.1 },
		);
		if (container) {
			observer.observe(container);
		}

		// Cleanup - ensure scroll events are always re-enabled
		return () => {
			if (observer && container) {
				observer.unobserve(container);
			}
			if (cleanup) {
				cleanup();
			}
		};
	}, []);

	return (
		<section css={sectionCss}>
			<div
				css={[
					desktopCss,
					desktopLeftAssetCss,
					css({
						transform: interacting ? 'translateX(-120px)' : 'translateX(0)',
					}),
				]}
			>
				<Image
					src="https://dam-cdn.atl.orangelogic.com/AssetLink/ih4h04rnm06355wnp2arca3f4gkg0258.webp"
					alt=""
					style={desktopAssetStyle}
				/>
			</div>
			<div
				css={[
					desktopCss,
					desktopRightAssetCss,
					css({
						transform: interacting ? 'translateX(120px)' : 'translateX(0)',
					}),
				]}
			>
				<Image
					src="https://dam-cdn.atl.orangelogic.com/AssetLink/3qji3u3j76e5i63yjsj7pnko2npm30n4.webp"
					alt=""
					style={desktopAssetStyle}
				/>
			</div>
			<div ref={contentContainerRef} css={uniformSlotContainerCss}>
				<UniformSlot name="chatContent" />
			</div>
			<div css={mobileCss}>
				<Image
					src="https://dam-cdn.atl.orangelogic.com/AssetLink/6ts3bd426400kq6ds07w1208q7a038r0.webp"
					alt=""
					style={imageStyles}
					css={mobileCss}
				/>
			</div>
			<div css={tabletCss}>
				<Image
					src="https://dam-cdn.atl.orangelogic.com/AssetLink/6325jfp76jcjym74os216mopusw7m420.webp"
					alt=""
					style={imageStyles}
				/>
			</div>
		</section>
	);
};

export default UniformRovoChatContainer;

const desktopAssetStyle = {
	width: '787px',
};

const desktopLeftAssetCss = css({
	transition: 'transform 600ms ease-in-out',

	position: 'absolute',
	zIndex: 1,
	insetInlineStart: '-465px',
	width: '787px',
	height: 'auto',
	overflow: 'hidden',

	[media.only.md]: {
		insetInlineStart: '-680px',
	},
});

const desktopRightAssetCss = css({
	transition: 'transform 600ms ease-in-out',

	position: 'absolute',
	zIndex: 1,
	insetInlineEnd: '-465px',
	width: '787px',
	height: 'auto',
	overflow: 'hidden',

	[media.only.md]: {
		insetInlineEnd: '-680px',
	},
});

const desktopCss = css({
	[media.below.md]: {
		display: 'none',
	},
});

const tabletCss = css({
	[media.above.md]: {
		display: 'none',
	},
	[media.below.sm]: {
		display: 'none',
	},
});

const mobileCss = css({
	[media.above.sm]: {
		display: 'none',
	},
});

const imageStyles = {
	width: '100%',
	display: 'block',
};

const sectionCss = css({
	overflow: 'hidden',
	position: 'relative',
	backgroundColor: wacToken('color.neutral.light100'),
});
